import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faCircle } from '@fortawesome/pro-regular-svg-icons'
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useEffect, useState } from 'react'

interface JumboButtonProps<T> {
  name: string
  title: React.ReactElement | string
  description?: React.ReactElement | string
  value: T
  checked: boolean
  className?: string
  invalid?: boolean
  disabled?: boolean
  styled?: boolean
  onChange: (value: T) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
}

function JumboButton<T>(props: JumboButtonProps<T>): React.ReactElement {
  const { name, value, checked, disabled, invalid, styled, title, description, onChange } = props
  const cardClassNameList: string[] = ['card card-select h-100 w-100 shadow']
  let cardStateClassName = ''
  if (invalid) {
    cardStateClassName = 'border-danger'
  }
  if (disabled) {
    cardStateClassName = 'disabled'
  }
  if (checked && !disabled) {
    cardStateClassName = 'selected'
  }
  if (invalid) {
    cardClassNameList.push('is-invalid')
  }
  if (styled) {
    cardClassNameList.push('styled')
  }
  cardClassNameList.push(cardStateClassName)
  const icon: IconProp = checked ? faCheckCircle : faCircle
  return (
    <div
      className={cardClassNameList.join(' ').trim()}
      onClick={() => {
        if (!disabled) {
          onChange(value)
        }
      }}
    >
      <div className="card-body d-flex">
        <div className="form-check my-auto">
          <input
            type="radio"
            value={value?.toString()}
            checked={checked}
            disabled={disabled}
            name={name}
            className="form-check-input visually-hidden"
            onChange={() => {
              if (!disabled) {
                onChange(value)
              }
            }}
            onBlur={props?.onBlur}
          />
          <label className="custom-control-label form-check-label px-2">
            <FontAwesomeIcon icon={icon} className="custom-control-icon" />
            <div className="fw-bold">{title}</div>
            {typeof description !== 'undefined' && (
              <small className="d-block m-0">{description}</small>
            )}
          </label>
        </div>
      </div>
    </div>
  )
}

export interface JumboOption<T> {
  name: string
  title: React.ReactElement | string
  description?: React.ReactElement | string
  value: T
  disabled?: boolean
}

export interface JumboSelectProps<T> {
  value?: T
  types: JumboOption<T>[]
  disabled?: boolean
  invalid?: boolean
  className?: string
  compare?: (value: any, optionValue: any) => boolean
  onChange: (value: T) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
}

export function JumboSelect<T>(props: JumboSelectProps<T>): React.ReactElement {
  const [value, setValue] = useState<T | undefined>(props.value)
  const compare: (value?: T, optionValue?: T) => boolean = props.compare
    ? props.compare
    : (value, optionValue) =>
        (!value && !optionValue) || JSON.stringify(value) === JSON.stringify(optionValue)

  useEffect(() => {
    setValue(props?.value)
  }, [props])

  const types: JumboButtonProps<T>[] = props.types.map((t: JumboOption<T>) => ({
    name: t.name,
    title: t.title,
    description: t.description,
    value: t.value,
    checked: compare(value, t.value),
    disabled: t.disabled,
    invalid: props?.invalid,
    onChange: (value: T) => {
      setValue(value)
      if (props?.onChange) {
        props?.onChange(value)
      }
    },
    onBlur: props?.onBlur,
  }))
  return (
    <>
      {types.map((t: JumboButtonProps<T>, i: number) => (
        <div className={props.className || 'col mt-3'} key={['jumbo_select', i].join(':')}>
          {JumboButton(t)}
        </div>
      ))}
    </>
  )
}
